import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Share2, CalendarPlus, Users } from "lucide-react";
import AppointmentRequestModal from "../components/schedule/AppointmentRequestModal";
import WaitlistModal from "../components/schedule/WaitlistModal";
import { formatTimeRange } from "../lib/eventTime";

// Standalone event landing page (/e/:id) — deliberately OUTSIDE the site
// layout: no navbar, no footer, just the event. Made to be shared as a link
// on WhatsApp/posters; fully responsive; register + waitlist built in.

const toYouTubeEmbed = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be"))
      return `https://www.youtube-nocookie.com/embed${u.pathname}`;
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/shorts/"))
        return `https://www.youtube-nocookie.com/embed/${u.pathname.split("/")[2]}`;
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube-nocookie.com/embed/${v}`;
    }
  } catch {
    /* not a URL we can embed */
  }
  return null;
};

const formatLongDate = (value) => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
};

// How far a timezone sits from UTC at a given instant, in ms.
const zoneOffsetMs = (date, timeZone) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
      .formatToParts(date)
      .map((p) => [p.type, p.value])
  );
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second)
  );
  return asUtc - date.getTime();
};

// The exact instant an event begins.
//
// eventDate is a date-only value at midnight UTC and startTime is a wall-clock
// string like "16:00"; neither on its own says when the event starts. Counting
// to eventDate alone targeted midnight UTC, which for a 4 PM Atlanta event ran
// out twenty hours early — the evening BEFORE the event, while devotees were
// still travelling.
export const eventStartInstant = (eventDate, startTime, timeZone) => {
  const base = new Date(eventDate);
  if (isNaN(base.getTime())) return null;
  const day = base.toISOString().slice(0, 10);
  const clock = /^([01]\d|2[0-3]):[0-5]\d$/.test((startTime || "").trim())
    ? startTime.trim()
    : "00:00";
  const zone = timeZone || "America/New_York";
  // Read the wall-clock time as if it were UTC, then shift by the venue's
  // offset at that moment so the result is the true instant.
  const naive = new Date(`${day}T${clock}:00.000Z`);
  if (isNaN(naive.getTime())) return null;
  return new Date(naive.getTime() - zoneOffsetMs(naive, zone));
};

// Live countdown parts (days/hours/minutes) — null once the moment passes.
const countdownTo = (value) => {
  const target = new Date(value).getTime();
  if (isNaN(target)) return null;
  const diff = target - Date.now();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
  };
};

export default function EventLanding() {
  const { id } = useParams();
  const base = import.meta.env.VITE_APP_API_BASE_URL || "";
  const apiRoot = base.replace(/\/scheduleRegistration\/?$/, "");

  const [event, setEvent] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | notfound | error
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${apiRoot}/events/${id}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw Object.assign(new Error("load failed"), { status: r.status });
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        setEvent(data?.data || null);
        setState(data?.data ? "ready" : "notfound");
      })
      .catch((err) => {
        if (cancelled) return;
        setState(err?.status === 404 ? "notfound" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [apiRoot, id]);

  useEffect(() => {
    if (!event?.eventDate) return;
    const startsAt = eventStartInstant(event.eventDate, event.startTime, event.eventTimezone);
    setCountdown(countdownTo(startsAt));
    const timer = setInterval(() => setCountdown(countdownTo(startsAt)), 30000);
    return () => clearInterval(timer);
  }, [event?.eventDate]);

  const booking = event?.booking || null;
  const isPast = event?.eventDate
    ? new Date(event.eventDate).getTime() < Date.now() - 24 * 60 * 60 * 1000
    : false;
  const canRegister = !isPast && booking && !booking.isFull;
  const isFull = !isPast && booking && booking.isFull;

  const scheduleItem = useMemo(() => {
    if (!event?.booking) return null;
    const formatted = formatLongDate(event.eventDate) || "Not Available";
    return {
      id: event.booking.scheduleId,
      type: "event",
      availableSeats: event.booking.availableSeats,
      location: event.eventLocation,
      startDate: formatted,
      endDate: formatted,
      registrationFields: event.registrationFields || [],
      registrationNote: event.registrationNote || "",
      showSeatsRemaining: event.showSeatsRemaining !== false,
      // Carries the venue time onto the registration record, so confirmation
      // emails say when to arrive and not just which day.
      time: formatTimeRange(event.startTime, event.endTime) || "",
      request: true,
    };
  }, [event]);

  const googleCalendarUrl = useMemo(() => {
    if (!event?.eventDate) return null;
    const start = new Date(event.eventDate);
    if (isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
    const fmt = (d) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: event.eventName || "Event",
      dates: `${fmt(start)}/${fmt(end)}`,
      details: "In the divine presence of Pujya Swami Avdheshanand Giri Ji Maharaj.",
      location: event.eventLocation || "",
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }, [event]);

  const share = async () => {
    const url = window.location.href;
    const title = event?.eventName || "Event";
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch {
        /* user dismissed — fall through to copy */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${title} — ${url}`)}`, "_blank");
    }
  };

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#1c0f0a] font-serif text-[#f3e6c0]">
        <p>Loading…</p>
      </div>
    );
  }

  if (state !== "ready" || !event) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#1c0f0a] px-6 text-center font-serif text-[#f3e6c0]">
        <p className="text-2xl text-[#f5c45e]">Hari Om 🙏</p>
        <p className="mt-3">
          {state === "notfound"
            ? "This event could not be found — it may have concluded."
            : "Something went wrong. Please try again."}
        </p>
        <a
          href="https://www.avdheshanandgmission.org"
          className="mt-6 text-sm text-[#f5c45e] underline-offset-4 hover:underline"
        >
          avdheshanandgmission.org
        </a>
      </div>
    );
  }

  const gallery = (event.gallery || []).filter((g) => g?.src);
  const videos = (event.videos || []).filter((v) => v?.url);
  const longDate = formatLongDate(event.eventDate);
  const timeRange = formatTimeRange(event.startTime, event.endTime);

  return (
    <div className="min-h-screen bg-[#1c0f0a] font-serif text-[#2a1a12]">
      {/* Identity bar — the page is shared as a bare link, so the mission is
          named in solid gold before anything else loads. */}
      <div className="flex items-center justify-center gap-3 bg-[#f5c45e] px-6 py-3">
        <img
          src="/assets/Avdheshanandg mission logo.png"
          alt="AvdheshanandG Mission"
          className="h-8 w-auto"
        />
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#3d0000]">
          AvdheshanandG Mission
        </span>
      </div>

      {/* Hero */}
      <header className="relative">
        <div className="absolute inset-0">
          {event.eventImage && (
            // The poster already carries the event title, so at full clarity it
            // duplicates and fights the real heading. Opacity alone never fixed
            // it — white poster text stays legible under any wash. Blurring
            // reduces it to colour and atmosphere with no readable text; the
            // scale hides the soft edges blur leaves at the frame.
            <img
              src={event.eventImage}
              alt=""
              aria-hidden="true"
              className="h-full w-full scale-110 object-cover blur-2xl"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[#2a0a05]/85 via-[#2a0a05]/88 to-[#1c0f0a]" />
        </div>

        <div className="relative mx-auto flex min-h-[88vh] max-w-3xl flex-col items-center justify-center px-6 py-16 text-center text-white">
          <h1 className="text-4xl leading-tight sm:text-5xl">{event.eventName}</h1>
          <p className="mx-auto mt-4 h-1 w-24 rounded bg-[#f5c45e]/80" aria-hidden />

          {/* Details as a label/value ledger rather than centred icon lines —
              a long venue address stays readable, and every row lines up. */}
          <dl className="mt-8 w-full max-w-md text-left">
            {[
              ["Date", longDate],
              ["Time", timeRange],
              ["Format", event.eventFormat?.trim()],
              ["Venue", event.eventLocation],
            ]
              .filter(([, value]) => value)
              .map(([label, value], index, rows) => (
                <div
                  key={label}
                  className={`flex items-baseline justify-between gap-5 py-3 ${
                    index < rows.length - 1 ? "border-b border-[#f5c45e]/20" : ""
                  }`}
                >
                  <dt className="shrink-0 text-[10px] font-bold uppercase tracking-[0.2em] text-[#f5c45e]">
                    {label}
                  </dt>
                  <dd className="text-right text-sm text-white sm:text-base">{value}</dd>
                </div>
              ))}
          </dl>

          {countdown && (
            <div className="mt-8 flex gap-3">
              {[
                { value: countdown.days, label: "days" },
                { value: countdown.hours, label: "hrs" },
                { value: countdown.minutes, label: "min" },
              ].map((part) => (
                <div
                  key={part.label}
                  className="min-w-[74px] rounded-lg border border-white/20 bg-white/10 px-4 py-3 backdrop-blur"
                >
                  <p className="text-3xl font-semibold text-[#f5c45e]">{part.value}</p>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-white/70">
                    {part.label}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            {isPast && (
              <span className="rounded-lg bg-white/15 px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white/80">
                This event has concluded
              </span>
            )}
            {canRegister && (
              <button
                type="button"
                onClick={() => setRegistrationOpen(true)}
                className="rounded-lg border border-[#f5c45e] bg-[#5a0000] px-9 py-3.5 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-xl transition hover:bg-[#7a0000]"
              >
                Register — Free QR Entry
              </button>
            )}
            {isFull && (
              <button
                type="button"
                onClick={() => setWaitlistOpen(true)}
                className="rounded-lg border border-white/60 bg-white/10 px-7 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-white backdrop-blur transition hover:bg-white/20"
              >
                Full — Join the waitlist
              </button>
            )}
            <button
              type="button"
              onClick={share}
              className="inline-flex items-center gap-2 rounded-lg border border-white/40 px-5 py-3.5 text-sm text-white/90 transition hover:bg-white/10"
            >
              <Share2 size={16} />
              {copied ? "Link copied!" : "Share"}
            </button>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-sm text-white/75">
            {canRegister && booking && event.showSeatsRemaining !== false && (
              <span className="inline-flex items-center gap-1.5">
                <Users size={15} className="text-[#f5c45e]" />
                {booking.availableSeats} seat{booking.availableSeats === 1 ? "" : "s"} remaining
              </span>
            )}
            {googleCalendarUrl && !isPast && (
              <a
                href={googleCalendarUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 underline-offset-4 hover:underline"
              >
                <CalendarPlus size={15} className="text-[#f5c45e]" />
                Add to calendar
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="bg-[#fdf8f1]">
        {/* The poster, shown properly. It is the only place the artwork is
            legible now that the hero uses it purely as blurred atmosphere. */}
        {event.eventImage && (
          <section className="mx-auto max-w-3xl px-6 pt-12">
            <img
              src={event.eventImage}
              alt={event.eventName}
              className="mx-auto w-full rounded-xl border border-[#e6d6bd] shadow-lg"
            />
          </section>
        )}

        <section className="mx-auto max-w-3xl px-6 py-14">
          <h2 className="text-xs font-bold uppercase tracking-[0.3em] text-[#9d1f1c]">
            About this gathering
          </h2>
          <p className="mt-4 whitespace-pre-line text-lg leading-8 text-[#3d2b20]">
            {event.description}
          </p>
        </section>

        {gallery.length > 0 && (
          <section className="mx-auto max-w-5xl px-6 pb-14">
            <h2 className="text-xs font-bold uppercase tracking-[0.3em] text-[#9d1f1c]">
              Glimpses
            </h2>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {gallery.map((item, index) => (
                <figure key={index} className="overflow-hidden rounded-lg">
                  <img
                    src={item.src}
                    alt={item.alt || event.eventName}
                    loading="lazy"
                    className="aspect-square h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  {item.caption && (
                    <figcaption className="mt-1 text-xs text-[#7a6754]">{item.caption}</figcaption>
                  )}
                </figure>
              ))}
            </div>
          </section>
        )}

        {videos.length > 0 && (
          <section className="mx-auto max-w-3xl px-6 pb-14">
            <h2 className="text-xs font-bold uppercase tracking-[0.3em] text-[#9d1f1c]">Watch</h2>
            <div className="mt-5 space-y-6">
              {videos.map((video, index) => {
                const embed = toYouTubeEmbed(video.url);
                return (
                  <div key={index}>
                    {embed ? (
                      <div
                        className={
                          video.type === "reel"
                            ? "mx-auto aspect-[9/16] max-w-[320px] overflow-hidden rounded-xl"
                            : "aspect-video overflow-hidden rounded-xl"
                        }
                      >
                        <iframe
                          src={embed}
                          title={video.title || `Video ${index + 1}`}
                          className="h-full w-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <video src={video.url} controls className="w-full rounded-xl" />
                    )}
                    {video.title && <p className="mt-2 text-sm text-[#7a6754]">{video.title}</p>}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {(canRegister || isFull) && (
          <section className="bg-[#5a0000] px-6 py-14 text-center text-white">
            <p className="text-2xl">Will you join us?</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-white/80">
              {canRegister
                ? "Registration is free — every guest receives a personal QR entry pass by email."
                : "The event is currently full. Join the waitlist and we will email you the moment seats open."}
            </p>
            <button
              type="button"
              onClick={() => (canRegister ? setRegistrationOpen(true) : setWaitlistOpen(true))}
              className="mt-6 rounded-lg bg-[#f5c45e] px-9 py-3.5 text-sm font-bold uppercase tracking-[0.16em] text-[#3d0000] transition hover:bg-[#ffd784]"
            >
              {canRegister ? "Register now" : "Join the waitlist"}
            </button>
          </section>
        )}

        <footer className="bg-[#1c0f0a] px-6 py-8 text-center text-sm text-[#c9a24b]">
          <p className="text-[#f3e6c0]">With gratitude,</p>
          <p>AvdheshanandG Mission</p>
          <a
            href="https://www.avdheshanandgmission.org"
            className="mt-2 inline-block text-xs tracking-widest underline-offset-4 hover:underline"
          >
            avdheshanandgmission.org
          </a>
        </footer>
      </main>

      {registrationOpen && scheduleItem && (
        <AppointmentRequestModal
          isOpen={registrationOpen}
          onClose={() => setRegistrationOpen(false)}
          scheduleItem={scheduleItem}
          onSubmit={() => {}}
        />
      )}

      <WaitlistModal
        isOpen={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        scheduleId={booking?.scheduleId}
        eventName={event.eventName}
      />
    </div>
  );
}
